import { Label } from "@/components/ui/label";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import { cn } from "@/lib/utils";
import { getOptions } from "@/lib/questionnaire";

const TEXT_INPUT_CLASS =
  "rounded-sm border-border bg-card text-sm leading-relaxed focus-visible:ring-2 focus-visible:ring-ring/40";

function ShortTextInput({ inputId, question, value, onChange }) {
  return (
    <Input
      id={inputId}
      type="text"
      value={value || ""}
      onChange={(e) => onChange(e.target.value)}
      placeholder={question.placeholder || ""}
      className={TEXT_INPUT_CLASS}
      autoComplete="off"
    />
  );
}

function AbnInput({ inputId, question, value, onChange }) {
  const handleChange = (e) => {
    // Allow digits, spaces, and dashes during entry; normalise on read by callers.
    const cleaned = e.target.value.replace(/[^0-9\s-]/g, "");
    onChange(cleaned);
  };
  const digits = (value || "").replace(/\D/g, "");
  const valid = digits.length === 11;
  const showHint = digits.length > 0;
  return (
    <div className="space-y-1.5">
      <Input
        id={inputId}
        type="text"
        inputMode="numeric"
        value={value || ""}
        onChange={handleChange}
        placeholder={question.placeholder || "11-digit ABN"}
        maxLength={20}
        className={cn(TEXT_INPUT_CLASS, "font-mono tracking-wider")}
        autoComplete="off"
      />
      {showHint && (
        <p
          className={cn(
            "font-mono-label text-[10px] uppercase tracking-[0.18em]",
            valid ? "text-success" : "text-muted-foreground"
          )}
        >
          {digits.length} of 11 digits {valid ? "\u2014 valid format" : ""}
        </p>
      )}
    </div>
  );
}

function DateInput({ inputId, value, onChange }) {
  return (
    <Input
      id={inputId}
      type="date"
      value={value || ""}
      onChange={(e) => onChange(e.target.value)}
      className={cn(TEXT_INPUT_CLASS, "w-fit min-w-[180px]")}
    />
  );
}

function LongTextInput({ inputId, question, value, onChange }) {
  return (
    <Textarea
      id={inputId}
      value={value || ""}
      onChange={(e) => onChange(e.target.value)}
      rows={3}
      placeholder={question.placeholder || ""}
      className={cn("resize-none", TEXT_INPUT_CLASS)}
    />
  );
}

function FreeTextInput({ inputId, value, onChange }) {
  return (
    <Textarea
      id={inputId}
      value={value || ""}
      onChange={(e) => onChange(e.target.value)}
      rows={4}
      placeholder="Plain English. No identifying details."
      className={cn("resize-none", TEXT_INPUT_CLASS)}
    />
  );
}

function RadioInput({ section, question, inputId, value, onChange }) {
  const options = getOptions(section, question);
  return (
    <RadioGroup
      value={value || ""}
      onValueChange={onChange}
      className="grid grid-cols-1 gap-2"
    >
      {options.map((opt) => {
        const selected = value === opt.value;
        return (
          <label
            key={opt.value}
            htmlFor={`${inputId}-${opt.value}`}
            className={cn(
              "group flex cursor-pointer items-start gap-3 rounded-sm border bg-card px-4 py-3 text-sm transition-colors",
              selected
                ? "border-primary bg-secondary text-foreground"
                : "border-border hover:border-border-strong hover:bg-secondary/50"
            )}
          >
            <RadioGroupItem
              id={`${inputId}-${opt.value}`}
              value={opt.value}
              className="mt-0.5 border-border-strong text-primary"
            />
            <span className="leading-relaxed text-foreground/90">{opt.label}</span>
          </label>
        );
      })}
    </RadioGroup>
  );
}

function renderInput({ section, question, inputId, value, onChange }) {
  switch (question.type) {
    case "shorttext":
      return <ShortTextInput inputId={inputId} question={question} value={value} onChange={onChange} />;
    case "abn":
      return <AbnInput inputId={inputId} question={question} value={value} onChange={onChange} />;
    case "date":
      return <DateInput inputId={inputId} value={value} onChange={onChange} />;
    case "longtext":
      return <LongTextInput inputId={inputId} question={question} value={value} onChange={onChange} />;
    case "text":
      return <FreeTextInput inputId={inputId} value={value} onChange={onChange} />;
    default:
      return <RadioInput section={section} question={question} inputId={inputId} value={value} onChange={onChange} />;
  }
}

export function QuestionCard({ section, question, index, value, onChange, displayNumber }) {
  const inputId = `q-${question.id}`;
  const numberLabel =
    typeof displayNumber === "number"
      ? String(displayNumber).padStart(2, "0")
      : `${section.code}.${String(index + 1).padStart(2, "0")}`;
  return (
    <div className="paper-card p-6 sm:p-8">
      <div className="flex items-start gap-4">
        <span className="font-mono-label mt-1 hidden text-[11px] uppercase tracking-[0.18em] text-muted-foreground sm:inline">
          {numberLabel}
        </span>
        <div className="flex-1 space-y-5">
          <div className="space-y-2">
            <Label htmlFor={inputId} className="font-display text-lg leading-snug text-foreground">
              {question.prompt}
              {question.optional && (
                <span className="ml-2 align-middle font-mono-label text-[10px] uppercase tracking-[0.18em] text-muted-foreground">
                  Optional
                </span>
              )}
            </Label>
            {question.helper && (
              <p className="text-xs leading-relaxed text-muted-foreground">{question.helper}</p>
            )}
          </div>
          {renderInput({ section, question, inputId, value, onChange })}
        </div>
      </div>
    </div>
  );
}
